/**
 * Playtest detail sheet (INVENTORY §2 row 4; prototype lines 990-1069).
 *
 * `selectors.3e#ptDetailVM` carries the whole `ptd` bag. Two things this file
 * owns rather than the selector, per the no-closures rule in `selectors.ts`:
 * the store actions behind `clrKind` / `actKind` / `fpUrl`, and `window.open`.
 *
 * Copy that is verbatim product promise (INVENTORY §9): the header note
 * ("USER-MAINTAINED — NO AUTO-SYNC" / "TRACKED · LIVE — CHECKED <NS AGO>"),
 * the off-Steam attribution block, the self-report note, and both scam
 * warnings.
 *
 * UNTRACK is two-tap on a non-auto playtest: the first tap arms it (`ptRmArm`)
 * and the button reads `SURE?`, the second removes it for the whole crew with
 * its cascading rows (docs/porting/P4b.md §7). An `auto` playtest is only muted
 * for me, so it stays a single tap — the same split the tracked sheet's `✕`
 * makes, sharing the one arm string.
 */

import { Avatar } from '../components/Avatar.tsx';
import { PtHeroArt } from '../components/PtArt.tsx';
import { Sheet } from '../components/Sheet.tsx';
import { useQd } from '../store/index.ts';
import { ptDetailVM } from '../store/selectors.3e.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

function Tri() {
  return (
    <svg width="7" height="6" viewBox="0 0 9 8" style={{ display: 'block', flex: 'none' }} aria-hidden="true">
      <rect x="4" y="0" width="1" height="2" fill="currentColor" />
      <rect x="3" y="2" width="3" height="2" fill="currentColor" />
      <rect x="2" y="4" width="5" height="2" fill="currentColor" />
      <rect x="1" y="6" width="7" height="2" fill="currentColor" />
    </svg>
  );
}

/** The ↗ glyph on the full-width primary (prototype line 1058). */
function Arrow() {
  return (
    <svg width="9" height="9" viewBox="0 0 9 9" style={{ display: 'block', flex: 'none' }} aria-hidden="true">
      <rect x="3" y="0" width="6" height="2" fill="currentColor" />
      <rect x="7" y="0" width="2" height="6" fill="currentColor" />
      <rect x="5" y="2" width="2" height="2" fill="currentColor" />
      <rect x="3" y="4" width="2" height="2" fill="currentColor" />
      <rect x="1" y="6" width="2" height="2" fill="currentColor" />
    </svg>
  );
}

export default function PtDetailSheet(): JSX.Element | null {
  const s = useQd();
  const v = ptDetailVM(s, Date.now());
  if (!v) return null;

  return (
    <Sheet
      label="Playtest detail sheet"
      maxHeight="90vh"
      hs
      onClose={s.closePtDet}
      header={{ title: v.headKind, titleClr: 'var(--acc2)', titleFs: '8.5px', note: v.headNote }}
    >
      {/* 150px hero */}
      <div style={{ position: 'relative', height: 150, flex: 'none', background: v.heroBg }}>
        <div style={{ position: 'absolute', inset: 0, background: 'var(--gt-checker)' }} />
        <PtHeroArt id={v.id} cands={v.cands} ini={v.ini} fs={20} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,transparent 30%,rgba(5,5,8,.92))' }} />
        <div
          style={{
            position: 'absolute',
            left: 14,
            bottom: 12,
            right: 14,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <div style={{ fontFamily: PIXEL, fontSize: 11, color: 'var(--gt-text)', lineHeight: 1.5 }}>{v.name}</div>
          <div style={{ fontFamily: MONO, fontSize: 9, color: 'var(--gt-text-3)' }}>{v.sub}</div>
        </div>
        {v.isOff ? (
          <div
            style={{
              position: 'absolute',
              right: 12,
              top: 10,
              fontFamily: PIXEL,
              fontSize: '6.5px',
              background: 'var(--acc2)',
              color: 'var(--gt-ink)',
              padding: '5px 6px',
            }}
          >
            {v.srcChip}
          </div>
        ) : null}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '14px 14px 18px' }}>
        {/* status banner */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 9,
            border: '1px solid ' + v.bnBd,
            background: v.bnBg,
            padding: '12px 13px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {v.bnLive ? (
              <span
                style={{
                  width: 8,
                  height: 8,
                  background: 'var(--acc)',
                  animation: 'gt-blink 1.1s steps(1) infinite',
                  flex: 'none',
                }}
              />
            ) : null}
            <div style={{ fontFamily: PIXEL, fontSize: 8, color: v.bnClr, lineHeight: 1.6, transform: 'translateY(0.13em)' }}>
              {v.bnTxt}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontFamily: MONO, fontSize: '9.5px', color: 'var(--gt-text-5)' }}>{v.bnSub}</div>
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              {v.sqs.map((q) => (
                <div key={q.key} style={{ width: 11, height: 11, background: q.bg, border: '1px solid ' + q.bd, marginLeft: q.ml }} />
              ))}
            </div>
            <div style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.05em', color: 'var(--acc)' }}>{v.sqLbl}</div>
          </div>
        </div>

        {/* off-Steam attribution (INVENTORY §9, line 2144) */}
        {v.isOff ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 11,
              border: '1px dashed var(--acc2)',
              background: 'rgba(240,90,190,.04)',
              padding: '11px 12px',
            }}
          >
            <Avatar style={{ flex: 'none' }} size={20} fs={7} ini={v.byIni} bg={v.byBg} hasAv={v.byHasAv} avBg={v.byAvBg} />
            <div style={{ fontFamily: MONO, fontSize: 9, lineHeight: 1.7, color: 'var(--gt-text-4)' }}>{v.attrTxt}</div>
          </div>
        ) : null}

        {/* people boxes */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {v.boxes.map((c) => (
            <div
              key={c.key}
              style={{
                border: '1px solid var(--gt-line)',
                background: 'var(--gt-panel)',
                padding: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 9,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  fontFamily: MONO,
                  fontSize: '8.5px',
                  letterSpacing: '.08em',
                  color: c.hc,
                }}
              >
                {c.tri ? <Tri /> : null}
                {c.h}
              </div>
              {c.rows.map((p) => (
                <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <Avatar style={{ flex: 'none' }} size={20} fs={6} ini={p.i} bg={p.bg} hasAv={p.hasAv} avBg={p.avBg} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--gt-text)' }}>{p.name}</div>
                    <div style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.03em', color: 'var(--gt-muted)' }}>
                      {p.meta}
                    </div>
                  </div>
                  {p.hasClr ? (
                    <div
                      onClick={() => {
                        if (p.clrKind === 'int') void s.ptToggleInt(v.id);
                        else void s.ptClearSt(v.id);
                      }}
                      style={{
                        border: '1px solid var(--gt-border-3)',
                        color: 'var(--gt-text-5)',
                        fontFamily: MONO,
                        fontSize: 9,
                        padding: '9px 10px',
                        cursor: 'pointer',
                        flex: 'none',
                      }}
                    >
                      ✕ CLEAR
                    </div>
                  ) : null}
                </div>
              ))}
              {c.none ? <div style={{ fontFamily: MONO, fontSize: 10, color: 'var(--gt-ghost)' }}>—</div> : null}
              {c.hasAct ? (
                <div
                  onClick={() => {
                    if (c.actKind) void s.ptReport(v.id, c.actKind);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px dashed var(--gt-border-3)',
                    color: 'var(--gt-text-3)',
                    fontFamily: MONO,
                    fontSize: '9.5px',
                    letterSpacing: '.03em',
                    height: 42,
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                  }}
                >
                  {c.act}
                </div>
              ) : null}
            </div>
          ))}
        </div>

        {/* Both kinds now, not off-Steam only: a Steam row's REQUESTED rung is
            self-reported too, and the note is the only place that says so. The
            selector writes a different sentence per kind. */}
        <div style={{ fontFamily: MONO, fontSize: '8.5px', lineHeight: 1.7, color: 'var(--gt-muted-2)' }}>{v.selfNote}</div>

        <div style={{ fontSize: 12, lineHeight: 1.6, color: 'var(--gt-text-4)' }}>{v.desc}</div>

        {/* link + provenance / scam warnings */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            border: '1px solid var(--gt-line)',
            background: 'var(--gt-panel)',
            padding: '11px 12px',
          }}
        >
          {v.hasLink ? (
            <a
              href={v.linkHref}
              target="_blank"
              rel="noreferrer"
              style={{
                border: '1px solid var(--acc)',
                color: 'var(--acc)',
                fontFamily: MONO,
                fontSize: 10,
                padding: '12px 11px',
                textDecoration: 'none',
                textAlign: 'center',
              }}
            >
              {v.linkTxt}
            </a>
          ) : null}
          <div style={{ fontFamily: MONO, fontSize: '8.5px', lineHeight: 1.7, color: 'var(--gt-muted-2)' }}>
            {v.note1}
            <br />
            {v.note2}
          </div>
        </div>

        {v.hasFp ? (
          <div
            onClick={() => {
              window.open(v.fpUrl, '_blank');
              // Only the SIGNUP hand-off asks; INSTALL is somebody already in.
              if (v.fpAsk) s.openPtAsk(v.id);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              background: 'var(--acc)',
              color: 'var(--gt-ink)',
              fontFamily: PIXEL,
              fontSize: '8.5px',
              padding: '17px 10px',
              lineHeight: 1,
              cursor: 'pointer',
            }}
          >
            {v.fpTxt}
            <Arrow />
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 8 }}>
          <div
            onClick={() => void s.ptToggleInt(v.id)}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              border: '2px solid ' + v.fiBd,
              color: v.fiClr,
              background: v.fiBg,
              fontFamily: PIXEL,
              fontSize: 8,
              height: 48,
              lineHeight: 1,
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            <svg width="9" height="8" viewBox="0 0 9 8" style={{ display: 'block', flex: 'none' }} aria-hidden="true">
              <rect x="4" y="0" width="1" height="2" fill="currentColor" />
              <rect x="3" y="2" width="3" height="2" fill="currentColor" />
              <rect x="2" y="4" width="5" height="2" fill="currentColor" />
              <rect x="1" y="6" width="7" height="2" fill="currentColor" />
            </svg>
            {v.fiTxt}
          </div>
          <div
            onClick={() => {
              if (v.auto || s.ptRmArm === v.id) void s.ptUntrack(v.id);
              else s.setPtRmArm(v.id);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid ' + v.unBd,
              color: v.unClr,
              fontFamily: MONO,
              fontSize: 10,
              height: 48,
              padding: '0 14px',
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            {v.unTxt}
          </div>
        </div>
      </div>
    </Sheet>
  );
}
